// REACT IMPORTS
import { Route, Routes, useLocation } from "react-router-dom";

// COMPONENT IMPORTS
import Login from "pages/auth/Login";
import Layout from "components/layout";
import Dashboard from "pages/dashboard";
import OtpVerification from "pages/auth/OtpVerification";
import SendOtp from "pages/auth/SendOtp";
import NotFound from "pages/NotFound";
import Payments from "pages/payments";
import PaymentActionRedirect from "pages/payments/PaymentAction";
import SubscriptionsAndPlans from "pages/subscriptionsAndPlans";
import Plans from "pages/subscriptionsAndPlans/Plans";
import Documents from "pages/documents";
import PaymentMethod from "pages/paymentMethod";
import UpdateCardRedirect from "pages/paymentMethod/UpdateCardRedirect";
import LoginViaCred from "pages/auth/LoginViaCred";
import SignUp from "pages/auth/SignUp";
import SignUpOtpVerification from "pages/auth/SignUpOtpVerification";
import SetPassword from "pages/auth/SetPassword";
import SignUpEmailAttached from "pages/auth/SignUpEmailAttached";
import RequestAccess from "pages/auth/RequestAccess";
import ForgotPassword from "pages/auth/ForgotPassword";
import ForgotOtpVerification from "pages/auth/ForgotOtpVerification";
import ResetPassword from "pages/auth/ResetPassword";
import MakePayment from "pages/makePayment";
import RequestSuccess from "pages/auth/RequestSuccess";
import ActionCenter from "pages/actionCenter";
import UserProfile from "pages/profile/UserProfile";
import SupportAndHelp from "pages/supportAndHelp";
import Notification from "pages/notification/Notification";
import TokenProtectedRoutes, {
  CheckClusterId,
  IsLoginProtectedRoutes,
  TokenClearRoute,
  TokenNavigation,
} from "TokenProtectedRoutes";
import SignUpSessionRoute from "components/auth/SignUpSessionRoute";
import PaymentLink from "pages/actionPayments/PaymentLink";
import Agreement from "pages/actionPayments/Agreement";
import RetryPayment from "pages/retryPayment";
import Quotes from "pages/actionPayments/Quotes";
import Invoice from "pages/actionPayments/Invoice";
import ProductLink from "pages/productLink";
import QuickCheckout from "pages/quickCheckout";
import SignUpPassword from "pages/auth/SignUpPassword";
import { useEffect } from "react";
import { resetOnRefresh } from "store/slices/commonSlice";
import { useAppDispatch } from "store/hooks";
import LegacyPaymentLinkRedirect from "pages/LegacyPaymentLinkRedirect";
import LegacyUpdateBillingRedirect from "pages/LegacyUpdateBillingRedirect";
import DocumentsPermissionRoute from "components/auth/DocumentsPermissionRoute";
import PortalAvailabilityRoute from "components/auth/PortalAvailabilityRoute";
import ApplePayDebugPanel from "components/payments/ApplePayDebugPanel";

/* ------------------------------------- ROUTING PAGE ------------------------------------- */

const App = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(resetOnRefresh());
  }, [location.pathname]);

  return (
    <PortalAvailabilityRoute>
      <ApplePayDebugPanel />
      <Routes>
        <Route
          path="/:clusterId/:leadId/:paymentLinkId/:plink"
          element={<LegacyPaymentLinkRedirect />}
        />
        <Route
          path="/update_billing.php"
          element={<LegacyUpdateBillingRedirect />}
        />
        <Route
          path="/payments/:type/:id"
          element={
            <TokenClearRoute>
              <PaymentActionRedirect />
            </TokenClearRoute>
          }
        />
        <Route
          path="/payment-methods/update/:id"
          element={
            <TokenClearRoute>
              <UpdateCardRedirect />
            </TokenClearRoute>
          }
        />

        {/* URL ROUTES EXTRA */}
        <Route path="" element={<Layout />}>
          <Route
            path="/subscriptions-plans/:type/:id"
            element={
              <TokenClearRoute>
                <Plans />
              </TokenClearRoute>
            }
          />
        </Route>

        {/* Other routes */}
        <Route path="" element={<Layout />}>
          <Route
            path="/:clusterId"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <Dashboard />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/dashboard"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <Dashboard />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/payments"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <Payments />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/subscriptions-plans"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <SubscriptionsAndPlans />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/documents"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <DocumentsPermissionRoute>
                      <Documents />
                    </DocumentsPermissionRoute>
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/payment-methods"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <PaymentMethod />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/action-center"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <ActionCenter />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/my-profile"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <UserProfile />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/notification"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <Notification />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/make-payment"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <MakePayment />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/support-help"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <IsLoginProtectedRoutes>
                    <SupportAndHelp />
                  </IsLoginProtectedRoutes>
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />

          {/* WITHOUT CLUSTER ID ROUTES */}
          <Route
            path="/agreement/:id"
            element={
              <TokenClearRoute>
                <Agreement />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/payment_link/:id"
            element={
              <TokenClearRoute>
                <PaymentLink />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <TokenClearRoute>
                <Quotes />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/quotes/:id/:type"
            element={
              <TokenClearRoute>
                <Quotes />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/invoice/:id"
            element={
              <TokenClearRoute>
                <Invoice />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/product/:id"
            element={
              <TokenClearRoute>
                <ProductLink />{" "}
              </TokenClearRoute>
            }
          />
          <Route
            path="/:clusterId/quick-checkout"
            element={
              <CheckClusterId>
                <TokenClearRoute>
                  <QuickCheckout />
                </TokenClearRoute>
              </CheckClusterId>
            }
          />
          <Route
            path="/view-payment/:id"
            element={
              <TokenClearRoute>
                <RetryPayment />{" "}
              </TokenClearRoute>
            }
          />
        </Route>

        {/* LOGIN ROUTES */}
        <Route>
          <Route path="/invalid" element={<NotFound />} />
          <Route
            path="/:clusterId/*"
            element={
              <CheckClusterId>
                <NotFound />
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/otp-verification"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <OtpVerification />
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/otp-send"
            element={
              <CheckClusterId>
                <TokenProtectedRoutes>
                  <SendOtp />
                </TokenProtectedRoutes>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/login"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <Login />
                </TokenNavigation>
              </CheckClusterId>
            }
          />

          <Route
            path="/:clusterId/login-via-cred"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <LoginViaCred />
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/sign-up"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <SignUpSessionRoute>
                    <SignUp />
                  </SignUpSessionRoute>
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/sign-up-password"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <SignUpSessionRoute>
                    <SignUpPassword />
                  </SignUpSessionRoute>
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/sign-up-email-attached"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <SignUpSessionRoute>
                    <SignUpEmailAttached />
                  </SignUpSessionRoute>
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/forgot-password"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <ForgotPassword />
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/reset-password"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <ResetPassword />
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/request-access"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <RequestAccess />
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/access-request-send-successfully"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <RequestSuccess />
                </TokenNavigation>
              </CheckClusterId>
            }
          />

          <Route
            path="/:clusterId/sign-up-otp-verification"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <SignUpSessionRoute>
                    <SignUpOtpVerification />
                  </SignUpSessionRoute>
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/forgot-otp-verification"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <ForgotOtpVerification />
                </TokenNavigation>
              </CheckClusterId>
            }
          />
          <Route
            path="/:clusterId/set-password"
            element={
              <CheckClusterId>
                <TokenNavigation>
                  <SignUpSessionRoute>
                    <SetPassword />
                  </SignUpSessionRoute>
                </TokenNavigation>
              </CheckClusterId>
            }
          />
        </Route>
      </Routes>
    </PortalAvailabilityRoute>
  );
};

export default App;
